import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { asDragSource } from "../../components/dragAndDrop/DraggableComponent";
import Icon from "../../components/interface/Icon";
import ItemCard from "../../components/itemCardView/ItemCardView";
import { RELATIONSHIP_VIEW_ROW } from "../../constants/identifiers";
import tableDropAction from "../../constants/tableDropAction";
import { PropTypeShapes } from "../../types";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";

// import debugUtils from "../../utils/debugUtils";

const dragCallbacks = {
  beginDrag: _props => {
    // TODO: Do we want to set isDragging or similar...?
  },

  canDrag: (props, dragSource, _e) => {
    return dragSource.component.canDrag();
  },

  getDragSourceParams: props => {
    return {
      itemId: props.id,
      itemType: props.type,
      sourceView: props.viewId,
      sourceViewType: RELATIONSHIP_VIEW_ROW
    };
  },

  getPreviewElement: props => {
    return renderItem(props, true);
  }
};

const clickWasCollapseIcon = e => hasAncestorOfClass(e.target, "collapseArrow");

const renderItem = (
  itemProps,
  isPreview,
  onChangeEditing,
  onLabelChange,
  onItemClick,
  onToggleChildren,
  onItemDoubleClick
) => {
  const onItemClickInner = e => {
    if (!clickWasCollapseIcon(e)) {
      onItemClick(e);
    }
  };

  let classes = ["relationshipTableRow"];
  if (itemProps.selected) {
    classes.push("selected");
  }

  if (itemProps.isFiltered) {
    classes.push("isFiltered");
  }

  if (itemProps.drawDropAction === tableDropAction.DropAboveRow) {
    classes.push("dropAbove");
  } else if (itemProps.drawDropAction === tableDropAction.DropBelowRow) {
    classes.push("dropBelow");
  } else if (itemProps.drawDropAction === tableDropAction.DropOnRow) {
    classes.push("dropOn");
  }
  if (itemProps.index % 2) {
    classes.push("odd");
  }
  let styles = {
    transform: `translateY(${itemProps.rect.top}px)`,
    height: `${itemProps.rect.height}px`,
    width: "100%"
  };
  if (isPreview) {
    styles.transform = "none";
    styles.position = "relative";
  }
  return (
    <div className={classes.join(" ")} style={styles}>
      <div
        className={`rEventCol indent${itemProps.indentationLevel > 6 ? "Deep" : itemProps.indentationLevel} ${
          itemProps.hasChildren ? "hasChildren" : ""
        }`}
        onClick={onItemClickInner}
        onDoubleClick={onItemDoubleClick}
      >
        {itemProps.hasChildren ? (
          <Icon
            className="collapseArrow"
            icon={itemProps.childrenExpanded ? "downArrow" : "rightArrow"}
            color="iconMono"
            onClick={onToggleChildren}
            canFocus={false}
          />
        ) : null}
        {/* {props.hasChildren
          ? null : <div className={`relationshipTableEventDot ${props.color}`} />} */}
        <div className={`relationshipTableEventDot ${itemProps.color}`} />
        <div className="rEventColText">
          <ItemCard
            {...itemProps.relationshipViewSettings.toObject()}
            label={itemProps.label}
            titleMetrics={itemProps.titleMetrics}
            dateTextSize={itemProps.dateTextSize}
            narrativeTextSize={itemProps.narrativeTextSize}
            durationTextSize={itemProps.durationTextSize}
            summaryMetrics={itemProps.summaryMetrics}
            identifierSize={itemProps.identifierSize}
            identifier={itemProps.displayId}
            relatedItemsByType={itemProps.relatedItemsByType}
            imagePath={itemProps.imagePath}
            isCompleted={itemProps.isCompleted}
            onLabelChange={onLabelChange}
            onChangeEditing={onChangeEditing}
            isActiveView={itemProps.isActiveView}
            isSelected={itemProps.selected}
            autoEditWhenBlank={itemProps.autoEditWhenBlank}
            typesById={itemProps.typesById}
            type={itemProps.type}
          />
        </div>
      </div>
    </div>
  );
};

class RelationshipRowCardView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.onChangeEditing = this.onChangeEditing.bind(this);
    this.onLabelChange = this.onLabelChange.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onItemDoubleClick = this.onItemDoubleClick.bind(this);
    this.onToggleChildren = this.onToggleChildren.bind(this);
    this._isEditing = false;
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("RelationshipRowCardView", this.props, nextProps, true);
  // }

  canDrag() {
    // Disable the drag and drop when editing inside the node
    // The drag and drop may impact on the click event and focusing.
    return !this._isEditing && this.props.canDrag && this.props.type;
  }

  onChangeEditing(value) {
    this._isEditing = value;
  }

  onLabelChange(value) {
    this.props.onLabelChange([this.props.id], value);
  }

  onItemClick(e) {
    this.props.onClick(this.props.id, getModifierKeysForEvent(e));
  }

  onItemDoubleClick(_e) {
    if (this.props.onShowInspector) {
      this.props.onShowInspector();
    }
  }

  onToggleChildren() {
    if (this.props.onToggleChildren) {
      this.props.onToggleChildren(this.props.id, !this.props.childrenExpanded);
    }
  }

  render() {
    return renderItem(
      this.props,
      false,
      this.onChangeEditing,
      this.onLabelChange,
      this.onItemClick,
      this.onToggleChildren,
      this.onItemDoubleClick
    );
  }
}

RelationshipRowCardView.propTypes = {
  viewId: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onToggleChildren: PropTypes.func,
  onLabelChange: PropTypes.func,
  selected: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  colGroups: PropTypeShapes.relationshipColumnGroupList.isRequired,
  columnData: PropTypeShapes.relationshipIntersectionMap.isRequired,
  color: PropTypes.string.isRequired,
  rect: PropTypeShapes.flatRect.isRequired,
  fontSize: PropTypes.number.isRequired,
  autoEditWhenBlank: PropTypes.bool.isRequired,

  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  displayId: PropTypes.string.isRequired,
  label: PropTypes.string,
  titleMetrics: PropTypeShapes.textMetrics.isRequired,
  dateTextSize: PropTypeShapes.textSize.isRequired,
  durationTextSize: PropTypeShapes.textSize.isRequired,
  narrativeTextSize: PropTypeShapes.textSize.isRequired,
  summaryMetrics: PropTypeShapes.textMetrics.isRequired,
  identifierSize: PropTypeShapes.size.isRequired,
  relatedItemsByType: ImmutablePropTypes.list.isRequired,
  imagePath: PropTypes.string,
  isCompleted: PropTypes.bool,

  hasChildren: PropTypes.bool.isRequired,
  childrenExpanded: PropTypes.bool.isRequired,
  indentationLevel: PropTypes.number.isRequired,

  relationshipViewSettings: PropTypeShapes.relationshipViewSettings.isRequired,

  typesById: ImmutablePropTypes.map.isRequired,

  canDrag: PropTypes.bool,
  isDraggingRow: PropTypes.bool,
  drawDropAction: PropTypes.string,
  onShowInspector: PropTypes.func,

  // From asDragSource
  isDragging: PropTypes.bool,
  dragIsFromSameSource: PropTypes.bool
};

export default asDragSource(RelationshipRowCardView, RELATIONSHIP_VIEW_ROW, dragCallbacks);
